import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import type { ProcedureCatalogItem, ProcedureCatalogUpdate } from '../../../utils/procedureCatalogApi';

export const ProcedureCatalogItemDrawer: React.FC<{
  isOpen: boolean;
  item: ProcedureCatalogItem;
  onClose: () => void;
  onSave: (data: ProcedureCatalogUpdate) => Promise<void>;
}> = ({ isOpen, item, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [procedureType, setProcedureType] = useState('OTHER');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState<string>('');
  const [requiresEquipment, setRequiresEquipment] = useState(false);
  const [notes, setNotes] = useState('');
  const [stockTracked, setStockTracked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setName(item.name || '');
    setProcedureType((item as any).procedure_type || 'OTHER');
    setDescription(item.description || '');
    setDuration((item as any).default_duration_minutes != null ? String((item as any).default_duration_minutes) : '');
    setRequiresEquipment(Boolean((item as any).requires_special_equipment));
    setNotes((item as any).notes || '');
    setStockTracked(Boolean((item as any).is_stock_tracked));
  }, [isOpen, item.id]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload: ProcedureCatalogUpdate = {
        name: name.trim(),
        description: description || null,
        is_stock_tracked: stockTracked,
        ...(procedureType ? ({ procedure_type: procedureType } as any) : {}),
        ...(duration === '' ? {} : ({ default_duration_minutes: Number(duration) } as any)),
        ...(typeof requiresEquipment === 'boolean' ? ({ requires_special_equipment: requiresEquipment } as any) : {}),
        ...(notes ? ({ notes } as any) : {}),
      };
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl border-l border-gray-200 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">Update diagnostic</div>
            <div className="text-xs text-gray-500 mt-0.5 truncate">Update diagnostic details and inventory settings.</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-gray-100 text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Procedure Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Procedure Type</label>
            <select
              value={procedureType}
              onChange={(e) => setProcedureType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="EXAM">Physical Exam</option>
              <option value="IMAGING">Imaging</option>
              <option value="VITALS">Vitals</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Duration (minutes)</label>
              <input
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <input
                type="checkbox"
                checked={requiresEquipment}
                onChange={(e) => setRequiresEquipment(e.target.checked)}
                className="w-4 h-4"
              />
              <div className="text-sm text-gray-900">Requires special equipment</div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={() => setStockTracked((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                stockTracked ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  stockTracked ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <div>
              <div className="text-sm font-medium text-gray-900">Track as inventory</div>
              <div className="text-xs text-gray-500">Enables on-hand, lots, and inventory reporting.</div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};



